import { Graphics, Container, FillGradient, type FederatedPointerEvent } from "pixi.js";
import { KeyLayout, PianoLayout } from "../../piano/piano-layout";
import { Piano } from "../../piano/piano";
import { Note } from "../../music/note";
import { BLACK_KEY_RATIO } from "../../piano/horizontal-projection";
import { PianoHintLabels } from "./piano-hint-labels";

// Cached gradients, shared across all PianoKeyView instances.
// These don't depend on key dimensions (they use normalised 0-1 offsets).
const GRADIENT_WHITE_BODY = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.00, color: '#eef0f5' },
    { offset: 1.00, color: '#dde0ea' },
  ],
});
const GRADIENT_WHITE_SHADOW = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.0, color: 'rgba(0,0,0,0.0)' },
    { offset: 1.0, color: 'rgba(10,12,30,0.13)' },
  ],
});
const GRADIENT_WHITE_SHADOW_PRESSED = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.0, color: 'rgba(0,0,0,0.0)' },
    { offset: 1.0, color: 'rgba(80,70,140,0.20)' },
  ],
});
const GRADIENT_WHITE_GLOW = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.0, color: 'rgba(124,116,200,0.18)' },
    { offset: 1.0, color: 'rgba(124,116,200,0.0)' },
  ],
});
const GRADIENT_BLACK_BODY = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.00, color: '#2a2d38' },
    { offset: 0.50, color: '#181b24' },
    { offset: 1.00, color: '#0d0e12' },
  ],
});
const GRADIENT_BLACK_SHADOW = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.0, color: 'rgba(0,0,0,0.0)' },
    { offset: 1.0, color: 'rgba(0,0,0,0.65)' },
  ],
});
const GRADIENT_BLACK_SHADOW_PRESSED = new FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0.0, color: 'rgba(0,0,0,0.0)' },
    { offset: 1.0, color: 'rgba(20,15,40,0.55)' },
  ],
});

function extractRGB(color: number): [number, number, number] {
  return [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff];
}

function blendOnWhite(color: number, alpha: number): number {
  const [r, g, b] = extractRGB(color);
  const blend = (c: number) => Math.round(c * alpha + 255 * (1 - alpha));
  return (blend(r) << 16) | (blend(g) << 8) | blend(b);
}

function darken(color: number, factor = 0.85): number {
  const [r, g, b] = extractRGB(color);
  const d = (c: number) => Math.round(c * factor);
  return (d(r) << 16) | (d(g) << 8) | d(b);
}

function toRgbaString(color: number, alpha: number): string {
  const [r, g, b] = extractRGB(color);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Cache for per-hint-color glow gradients. Keyed by the hint color number;
// gradients use normalised 0-1 offsets so they can be reused across keys
// regardless of key dimensions.
const HINT_GLOW_GRADIENT_CACHE = new Map<number, FillGradient>();

function getHintGlowGradient(color: number): FillGradient {
  const cached = HINT_GLOW_GRADIENT_CACHE.get(color);
  if (cached) return cached;
  const gradient = new FillGradient({
    type: 'linear',
    colorStops: [
      { offset: 0.0, color: toRgbaString(color, 0.6) },
      { offset: 1.0, color: toRgbaString(color, 0.1) },
    ],
  });
  HINT_GLOW_GRADIENT_CACHE.set(color, gradient);
  return gradient;
}

class PianoKeyView {
  private readonly graphic: Graphics;
  private readonly keyLayout?: KeyLayout;

  private whiteKeyWidth = 0;
  private containerHeight = 0;

  private width = 0;
  private height = 0;

  private pressed = false;
  private hintColor: number | null = null;

  constructor(
    container: Container,
    private readonly note: Note,
    private readonly piano: Piano,
    private readonly pianoLayout: PianoLayout
  ) {
    this.keyLayout = pianoLayout.getKeyByMidi(note.toMidi());

    this.graphic = new Graphics();
    this.bindInteractions(this.graphic);
    container.addChild(this.graphic);
  }

  setGeometry(whiteKeyWidth: number, height: number): void {
    this.whiteKeyWidth = whiteKeyWidth;
    this.containerHeight = height;
  }

  /**
   * La touche quitte la scène pour de bon.
   *
   * `removeChildren()` côté appelant détache sans détruire : à chaque
   * changement de fenêtre (chargement, transposition, recentrage), les 88
   * `Graphics` et leur géométrie restaient en mémoire jusqu'au passage du
   * ramasse-miettes de Pixi, qui ne tourne que pendant le rendu.
   */
  destroy(): void {
    this.graphic.destroy();
  }

  press(): void {
    if (this.pressed) return;
    this.pressed = true;
    this.draw();
  }

  release(): void {
    if (!this.pressed) return;
    this.pressed = false;
    this.draw();
  }

  setHint(color: number | null): void {
    if (this.hintColor === color) return;
    this.hintColor = color;
    this.draw();
  }

  draw(): void {
    if (!this.keyLayout) return;
    if (this.whiteKeyWidth <= 0 || this.containerHeight <= 0) return;

    // Keys are drawn in content space (offset 0); the keyboard container is
    // translated by PianoComponent to apply the shared horizontal offset.
    const rawWhiteWidth = this.whiteKeyWidth;
    const isBlack = this.keyLayout.color === 'black';

    // For white keys: derive x and width from snapped grid edges so adjacent
    // keys always share the exact same pixel boundary, no gaps, no overlaps.
    //   x     = round(index       * rawWidth)
    //   right = round((index + 1) * rawWidth)
    //   width = right - x
    //
    // For black keys: centre on the boundary between two white keys using the
    // same snapped grid, then derive a snapped width from the snapped rawWidth.
    let x: number;

    if (isBlack) {
      const blackW = Math.round(rawWhiteWidth * BLACK_KEY_RATIO);
      const centre = Math.round((this.keyLayout.whiteIndex + 1) * rawWhiteWidth);
      x = centre - Math.floor(blackW / 2);
      this.width  = blackW;
      this.height = Math.round(this.containerHeight * 0.60);
    } else {
      const x0 = Math.round(this.keyLayout.whiteIndex * rawWhiteWidth);
      const x1 = Math.round((this.keyLayout.whiteIndex + 1) * rawWhiteWidth);
      x = x0;
      this.width  = x1 - x0;
      this.height = Math.round(this.containerHeight);
    }

    this.graphic.x = x;
    this.graphic.y = 0;

    if (isBlack) {
      this.drawBlackKey();
    } else {
      this.drawWhiteKey();
    }
  }

  private drawWhiteKey(): void {
    const g = this.graphic;
    const w = this.width;
    const h = this.height;
    const rBottom = Math.min(6, w * 0.12);
    const hinted = !this.pressed && this.hintColor !== null;

    g.clear();

    // --- Body ---
    // pressed-without-hint fallback (light violet), global accent, not hand-tied.
    const bodyFill = this.pressed
      ? 0xb8b2e0
      : hinted
        ? blendOnWhite(this.hintColor!, 0.85)
        : GRADIENT_WHITE_BODY;

    g
      .moveTo(0, 0)
      .lineTo(w, 0)
      .lineTo(w, h - rBottom)
      .arcTo(w, h, w - rBottom, h, rBottom)
      .lineTo(rBottom, h)
      .arcTo(0, h, 0, h - rBottom, rBottom)
      .lineTo(0, 0)
      .closePath()
      .fill(bodyFill);

    // --- Bottom shadow ---
    const shadowH = Math.max(10, h * 0.16);
    g
      .roundRect(1, h - shadowH - 1, w - 2, shadowH, Math.max(1, rBottom - 1))
      .fill(this.pressed ? GRADIENT_WHITE_SHADOW_PRESSED : GRADIENT_WHITE_SHADOW);

    // --- Inner glow (top) ---
    // When the key is BOTH pressed and hinted, the glow takes the hint
    // colour : the press feedback adopts the chord/scale colour, giving
    // an unambiguous «&nbsp;you played a note from the focus&nbsp;» cue.
    if (this.pressed && this.hintColor !== null) {
      g
        .rect(1, 0, w - 2, Math.round(h * 0.5))
        .fill(getHintGlowGradient(this.hintColor));
    } else if (this.pressed) {
      g
        .rect(1, 0, w - 2, Math.round(h * 0.30))
        .fill(GRADIENT_WHITE_GLOW);
    } else if (hinted) {
      g
        .rect(1, 0, w - 2, Math.round(h * 0.5))
        .fill(getHintGlowGradient(this.hintColor!));
    }

    // --- Separator: 1px dark right edge + 1px light left edge ---
    g
      .rect(w - 1, 0, 1, h - rBottom)
      .fill(this.pressed ? 'rgba(60,50,110,0.35)' : 'rgba(15,18,40,0.25)');
    g
      .rect(0, 0, 1, h - rBottom)
      .fill(this.pressed ? 'rgba(180,170,255,0.12)' : 'rgba(255,255,255,0.60)');
  }

  private drawBlackKey(): void {
    const g = this.graphic;
    const w = this.width;
    const h = this.height;
    const rBottom = Math.round(Math.min(3, w * 0.12));
    const hinted = !this.pressed && this.hintColor !== null;

    g.clear();

    // --- Body ---
    // Pressed + hinted : adopt the hint colour directly so the black
    // key clearly «&nbsp;answers&nbsp;» when the player matches a note
    // of the focus.
    // pressed-without-hint fallback (dark violet), global accent, not hand-tied.
    const bodyFill = this.pressed
      ? this.hintColor !== null
        ? this.hintColor
        : 0x6a5aaa
      : hinted
        ? darken(this.hintColor!)
        : GRADIENT_BLACK_BODY;

    g
      .moveTo(0, 0)
      .lineTo(w, 0)
      .lineTo(w, h - rBottom)
      .arcTo(w, h, w - rBottom, h, rBottom)
      .lineTo(rBottom, h)
      .arcTo(0, h, 0, h - rBottom, rBottom)
      .lineTo(0, 0)
      .closePath()
      .fill(bodyFill);

    // --- Top edge ---
    const topColor =
      this.hintColor !== null
        ? toRgbaString(this.hintColor, this.pressed ? 0.6 : 0.4)
        : this.pressed
          ? 'rgba(150,140,200,0.30)'
          : 'rgba(120,130,160,0.35)';
    g.rect(1, 0, w - 2, 1).fill(topColor);

    // --- Bottom shadow ---
    const shadowH = Math.round(Math.max(8, h * 0.20));
    g
      .roundRect(0, h - shadowH, w, shadowH, rBottom)
      .fill(this.pressed ? GRADIENT_BLACK_SHADOW_PRESSED : GRADIENT_BLACK_SHADOW);
  }

  private bindInteractions(g: Graphics): void {
    g.eventMode = 'static';
    g.cursor = 'pointer';

    // Active pointers that pressed this key. Used as a fallback safety net:
    // on iOS WKWebView, Pixi's `pointerupoutside`/`pointercancel` is sometimes
    // not delivered when the system hijacks a touch (palm rejection, edge
    // gestures, multitouch confusion) — leaving the note "stuck" pressed.
    // We mirror the up/cancel events at the window level keyed by pointerId
    // so we always release.
    const activePointers = new Set<number>();

    const onWindowEnd = (event: PointerEvent) => {
      releasePointer(event.pointerId);
    };

    const releasePointer = (pointerId: number) => {
      if (!activePointers.delete(pointerId)) {
        return;
      }

      if (activePointers.size === 0) {
        window.removeEventListener('pointerup', onWindowEnd);
        window.removeEventListener('pointercancel', onWindowEnd);
      }

      this.piano.release(this.note);
    };

    g.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      this.piano.press(this.note);

      const pointerId = e.pointerId;

      if (pointerId !== undefined) {
        const wasEmpty = activePointers.size === 0;
        activePointers.add(pointerId);

        if (wasEmpty) {
          window.addEventListener('pointerup', onWindowEnd);
          window.addEventListener('pointercancel', onWindowEnd);
        }
      }
    });

    const localRelease = (e: FederatedPointerEvent) => {
      const pointerId = e.pointerId;

      if (pointerId !== undefined && activePointers.has(pointerId)) {
        releasePointer(pointerId);

        return;
      }

      this.piano.release(this.note);
    };

    g.on('pointerup', localRelease);
    g.on('pointerupoutside', localRelease);
    g.on('pointercancel', localRelease);
  }
}

export class PianoRenderer {
  private unsubscribePiano?: () => void;

  private keys: Map<number, PianoKeyView> = new Map<number, PianoKeyView>();

  /** Drawn after the keys, so it sits on top of them. */
  private readonly hintLabels: PianoHintLabels;

  private whiteKeyWidth: number = 0;
  private height: number = 0;

  private needRedraw = false;

  constructor(private stage: Container, private piano: Piano, private pianoLayout: PianoLayout) {
    pianoLayout.getWhiteKeys().forEach(key => {
      this.keys.set(key.midi, new PianoKeyView(stage, key.note, piano, pianoLayout));
    });

    pianoLayout.getBlackKeys().forEach(key => {
      this.keys.set(key.midi, new PianoKeyView(stage, key.note, piano, pianoLayout));
    });

    this.hintLabels = new PianoHintLabels(stage, pianoLayout);

    this.unsubscribePiano = this.piano.on((event) => {
      if (event.type === "hints_changed") {
        const hinted = new Map<number, number>();

        this.keys.forEach((key, midi) => {
          // Le halo prend les deux sources ; la pastille, seulement ce qu'une
          // surface d'apprentissage désigne. Voir `Piano.sounding`.
          key.setHint(this.piano.getKeyHint(midi) ?? null);

          const shown = this.piano.getHint(midi);

          if (shown !== undefined) {
            hinted.set(midi, shown);
          }
        });

        this.hintLabels.setHints(hinted);

        return;
      }

      if (event.type !== 'press' && event.type !== 'release') return;

      const midi = event.note.toMidi();
      const key = this.keys.get(midi);

      if (event.type === "press") {
        key?.press();
      } else if (event.type === "release") {
        key?.release();
      }
    });
  }

  /**
   * Set the clamped white-key width (shared authority) and keyboard height.
   * The horizontal offset is applied at the container level by PianoComponent,
   * so a pan never triggers a key redraw, only a geometry change does.
   */
  setGeometry(whiteKeyWidth: number, height: number) {
    if (this.whiteKeyWidth === whiteKeyWidth && this.height === height) return;

    this.whiteKeyWidth = whiteKeyWidth;
    this.height = height;

    this.keys.forEach(key => key.setGeometry(whiteKeyWidth, height));
    this.hintLabels.setWhiteKeyWidth(whiteKeyWidth);

    this.needRedraw = true;
  }

  draw(): void {
    if (!this.needRedraw) return;

    this.keys.forEach(key => key.draw());

    this.needRedraw = false;
  }

  /**
   * Clean up internal state without destroying the stage container.
   * Used when rebuilding the renderer in-place (e.g., after viewport change).
   */
  cleanup(): void {
    this.unsubscribePiano?.();
    this.unsubscribePiano = undefined;

    for (const key of this.keys.values()) {
      key.destroy();
    }

    this.keys.clear();
    this.hintLabels.destroy();
  }

  async destroy(): Promise<void> {
    this.cleanup();

    if (this.stage) {
      this.stage.destroy({ children: true });
    }
  }
}
